import { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader, StatCard } from "@/components/StatCard";
import OpportunityCharts from "@/components/OpportunityCharts";
import { AddressHealthCard } from "@/components/AddressHealthCard";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Users2, UserCheck, Rocket, DollarSign, CreditCard, XCircle, RotateCcw,
  Mail, Voicemail, Copy, MessageSquareReply, HeartHandshake, Trophy, Bot, PhoneCall,
} from "lucide-react";

const money = (n) => `$${(Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}`;

export default function AdminDashboard() {
  const [a, setA] = useState(null);
  useEffect(() => { api.get("/admin/analytics").then((r) => setA(r.data)); }, []);

  const groups = [
    { title: "Accounts", cards: [
      { label: "MLO Accounts", value: a?.total_mlos, icon: Users2, accent: "bg-slate-900 text-amber-400" },
      { label: "Active MLOs", value: a?.active_mlos, icon: UserCheck, accent: "bg-emerald-100 text-emerald-700" },
    ]},
    { title: "Revenue", cards: [
      { label: "Prospecting Runs", value: a?.total_runs, icon: Rocket, accent: "bg-blue-100 text-blue-700" },
      { label: "Revenue (incl. lead sales)", value: money(a?.revenue), icon: DollarSign, accent: "bg-amber-100 text-amber-700" },
      { label: `Marketplace Lead Sales (${a?.market_sales ?? 0})`, value: money(a?.market_revenue), icon: DollarSign, accent: "bg-emerald-100 text-emerald-700" },
      { label: "Approved Lead Refunds", value: money(a?.refunds_approved), icon: RotateCcw, accent: "bg-red-100 text-red-700" },
      { label: "Net Revenue", value: money(a?.net_revenue), icon: DollarSign, accent: "bg-slate-900 text-amber-400" },
      { label: "Payments", value: a?.payments, icon: CreditCard, accent: "bg-indigo-100 text-indigo-700" },
      { label: "Failed Payments", value: a?.failed_payments, icon: XCircle, accent: "bg-red-100 text-red-700" },
      { label: "Refunds", value: a?.refunds, icon: RotateCcw, accent: "bg-slate-100 text-slate-600" },
    ]},
    { title: "Prospecting", cards: [
      { label: "Prospects", value: a?.prospects, icon: Users2, accent: "bg-blue-100 text-blue-700" },
      { label: "Prospects / Run", value: a?.prospects_per_run, icon: Rocket, accent: "bg-slate-100 text-slate-600" },
      { label: "Responses", value: a?.responses, icon: MessageSquareReply, accent: "bg-cyan-100 text-cyan-700" },
      { label: "Interested", value: a?.interested_leads, icon: HeartHandshake, accent: "bg-rose-100 text-rose-700" },
      { label: "Converted", value: a?.converted_leads, icon: Trophy, accent: "bg-emerald-100 text-emerald-700" },
    ]},
    { title: "Outreach", cards: [
      { label: "Emails Sent", value: a?.emails_sent, icon: Mail, accent: "bg-indigo-100 text-indigo-700" },
    ]},
    { title: "Integrations", cards: [
      { label: "Twin Usage", value: a?.twin_usage, icon: Bot, accent: "bg-slate-900 text-amber-400" },
      { label: "Twin Failures", value: a?.twin_failures, icon: XCircle, accent: "bg-red-100 text-red-700" },
    ]},
  ];

  return (
    <div>
      <PageHeader title="Administrator Overview" subtitle="Platform-wide activity across all MLO accounts." />
      {groups.map((g) => (
        <div key={g.title} className="mb-6">
          <h3 className="mb-3 text-xs font-semibold uppercase tracking-wider text-slate-500">{g.title}</h3>
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
            {g.cards.map((c) => <StatCard key={c.label} {...c} value={c.value ?? "—"} />)}
          </div>
        </div>
      ))}
      <AddressHealthCard />
      <div className="mb-6" data-testid="admin-analytics">
        <h3 className="mb-3 text-xs font-semibold uppercase tracking-wider text-slate-500">Lead Analytics</h3>
        <OpportunityCharts breakdown={a?.opportunity_breakdown} runs={a?.runs_breakdown} />
      </div>
      {a?.runs_by_mlo?.length > 0 && (
        <Card className="p-5">
          <h3 className="mb-3 font-display font-bold text-slate-900">Runs by MLO</h3>
          <div className="space-y-2">
            {a.runs_by_mlo.map((r) => (
              <div key={r.mlo_id} className="flex items-center justify-between border-b border-slate-100 pb-2 text-sm">
                <span className="text-slate-700">{r.name || r.mlo_id}</span>
                <Badge variant="outline">{r.runs} runs</Badge>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}
